import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverCloseButton,
  ButtonGroup,
  VStack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { TextArea } from '@gamma/ui/form-fields';
import NotesSharpIcon from '@gamma/ui/icons/NotesSharpIcon';
import CloseOutlinedIcon from '@gamma/ui/icons/CloseOutlinedIcon';
import { useDetectionBulkActionsContext } from '../../context';
import { i18n } from '@investigator/shared/localization';

enum AddNoteLocalizations {
  noteRequired = 'Note is required',
  noteTooLong = 'Note must be less than 1000 characters',
  confirmLargeSelection = 'Confirm Large Selection',
  confirmAddNoteMessage = 'You are about to add a note to {0} detections. This operation may take a moment to complete.',
  addNote = 'Add Note',
  note = 'Note',
  back = 'Back',
  cancel = 'Cancel',
  confirm = 'Confirm',
}

const NOTE_LENGTH_MAX = 1000;

interface AddNotePopoverProps {
  confirmationThreshold: number;
}

export const AddNotePopover = React.memo(
  ({ confirmationThreshold }: AddNotePopoverProps): JSX.Element => {
    AddNotePopover.displayName = 'AddNotePopover';
    const {
      selectedIds,
      handleAddNotes,
      loadingStates,
      isSelectAllMode,
      totalDetectionsCount,
    } = useDetectionBulkActionsContext();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const { controls } = i18n.pages.detections;

    const selectionCount = isSelectAllMode
      ? totalDetectionsCount
      : selectedIds.length;
    const requiresConfirmation = selectionCount > confirmationThreshold;

    const handleClose = useCallback(() => {
      setNote('');
      setError(null);
      setShowConfirmation(false);
      onClose();
    }, [onClose]);

    // Close popover when operation completes successfully (selections cleared)
    useEffect(() => {
      if (!loadingStates.note && selectedIds.length === 0 && isOpen) {
        handleClose();
      }
    }, [loadingStates.note, selectedIds.length, isOpen, handleClose]);

    const handleConfirm = useCallback(() => {
      handleAddNotes(note);
    }, [note, handleAddNotes]);

    const handleInitialSubmit = useCallback(() => {
      setError(null);

      if (!note.trim()) {
        setError(controls.noteRequired || AddNoteLocalizations.noteRequired);
        return;
      }

      if (note.length > NOTE_LENGTH_MAX) {
        setError(controls.noteTooLong || AddNoteLocalizations.noteTooLong);
        return;
      }

      if (requiresConfirmation) {
        setShowConfirmation(true);
      } else {
        handleConfirm();
      }
    }, [
      note,
      requiresConfirmation,
      handleConfirm,
      controls.noteRequired,
      controls.noteTooLong,
    ]);

    const handleCancelConfirmation = useCallback(() => {
      setShowConfirmation(false);
    }, []);

    return (
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={handleClose}
        placement="bottom-start"
      >
        <PopoverTrigger>
          <Button
            leftIcon={<NotesSharpIcon />}
            variant="solid"
            colorScheme="gray"
            size="sm"
            isLoading={loadingStates.note}
            isDisabled={selectedIds.length === 0}
          >
            {controls.addNote || AddNoteLocalizations.addNote}
          </Button>
        </PopoverTrigger>
        <PopoverContent minW="320px" maxW="360px">
          <PopoverCloseButton top={3}>
            <CloseOutlinedIcon size="sm" />
          </PopoverCloseButton>
          <PopoverHeader fontWeight="bold" p={4}>
            {controls.addNote || AddNoteLocalizations.addNote}
          </PopoverHeader>
          <PopoverBody p={4}>
            {showConfirmation ? (
              <Alert
                status="warning"
                bg="background.layer-00.default"
                borderWidth="1px"
                borderColor="yellow.500"
              >
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    {controls.confirmLargeSelection ||
                      AddNoteLocalizations.confirmLargeSelection}
                  </Text>
                  <Text>
                    {(
                      controls.confirmAddNoteMessage ||
                      AddNoteLocalizations.confirmAddNoteMessage
                    ).replace('{0}', String(selectionCount))}
                  </Text>
                </Box>
              </Alert>
            ) : (
              <VStack spacing={4} align="stretch">
                <TextArea
                  label={controls.note || AddNoteLocalizations.note}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  isRequired={true}
                  error={!!error}
                  name="note"
                />
                {error && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {error}
                  </Text>
                )}
              </VStack>
            )}
          </PopoverBody>
          <PopoverFooter display="flex" justifyContent="flex-end" p={4}>
            <ButtonGroup size="md" variant="solid">
              <Button
                colorScheme="gray"
                onClick={
                  showConfirmation ? handleCancelConfirmation : handleClose
                }
                isDisabled={loadingStates.note}
              >
                {showConfirmation
                  ? controls.back || AddNoteLocalizations.back
                  : controls.cancel || AddNoteLocalizations.cancel}
              </Button>
              <Button
                colorScheme="blue"
                onClick={showConfirmation ? handleConfirm : handleInitialSubmit}
                isLoading={loadingStates.note}
                isDisabled={
                  !showConfirmation && (!note.trim() || loadingStates.note)
                }
              >
                {showConfirmation
                  ? controls.confirm || AddNoteLocalizations.confirm
                  : controls.addNote || AddNoteLocalizations.addNote}
              </Button>
            </ButtonGroup>
          </PopoverFooter>
        </PopoverContent>
      </Popover>
    );
  },
);
